import React from "react";
import "./CaseStudy.css";
import { useNavigate } from "react-router-dom";

export default function CaniProject() {
  const navigate = useNavigate();

  return (
    <div className="case-page">

      <button className="case-back" onClick={() => navigate(-1)}>
        <svg viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.5 11.5L2.5 7.5M2.5 7.5L6.5 3.5M2.5 7.5H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back to Projects
      </button>

      <div className="case-hero">
        <h1>CANI — <em>Dual Website Launch</em></h1>
        <p className="case-hero-intro">
          A two-site digital presence for a federal services and AI-powered RevOps consultancy, delivered in 12 weeks with a shared design system spanning WordPress and HubSpot CMS.
        </p>
        <div className="case-meta-inline">
          <span><strong>Role</strong>UX Designer &amp; Web Developer</span>
          <span><strong>Duration</strong>Dec 2024 – Present</span>
          <span><strong>Tools</strong>Figma, WordPress, HubSpot CMS</span>
          <span><strong>Type</strong>Client Work</span>
        </div>
      </div>

      <div className="dual-hero">
        <div className="dual-hero-stage">

          <div className="browser-frame">
            <div className="browser-chrome">
              <div className="browser-dot" />
              <div className="browser-dot" />
              <div className="browser-dot" />
              <div className="browser-url">cani.com</div>
            </div>
            <img src="/images/cani-main-hero.png" alt="CANI main WordPress site" className="browser-screenshot" />
          </div>

          <div className="browser-frame">
            <div className="browser-chrome">
              <div className="browser-dot" />
              <div className="browser-dot" />
              <div className="browser-dot" />
              <div className="browser-url">revops.cani.com</div>
            </div>
            <img src="/images/cani-hubspot-hero.png" alt="CANI HubSpot RevOps site" className="browser-screenshot" />
          </div>

        </div>
        <p className="dual-hero-caption">Two sites. One brand. Two different decision points.</p>
      </div>

      <div className="case-body">

        {/* ── 01 The Problem ── */}
        <div className="case-section">
          <span className="case-section-num">01 — Problem</span>
          <h2>The <em>Problem</em></h2>
          <p>Contract Administration Network International (CANI) had a brand but barely a digital footprint. Before this project, their entire web presence was a single homepage — no service detail, no partner story, no way for prospects to evaluate fit or convert into a conversation. For a consultancy positioning itself across federal acquisition support, IT modernization, cybersecurity, and AI-driven RevOps, that gap was costing them credibility before any sales conversation could even start.</p>
          <p>The brief came with a twist: they didn't just need a better site. They needed <em>two</em> sites — and the second one needed to live somewhere different.</p>
        </div>

        <div className="case-divider" />

        {/* ── 02 Why Two Sites ── */}
        <div className="case-section">
          <span className="case-section-num">02 — Strategy</span>
          <h2>Why Two <em>Sites</em></h2>
          <p>The main CANI site needed to do credibility work: introduce the company, showcase the breadth of service offerings, and feature their strategic partnerships with HubSpot, HireVue, and Adaptive Security. Broad audience, broad message, soft conversion.</p>
          <p>The HubSpot site needed to do <em>conversion</em> work: a focused, opinionated landing experience for CANI's AI-Powered RevOps service — built on HubSpot, sold to growth-stage and federal clients ready to operationalize AI in their go-to-market motion.</p>

          <div className="case-callout">"The challenge wasn't building two sites. It was making them feel like one company telling two different stories at two different decision points."</div>

          <p>The platform decision flowed naturally from the goals. WordPress was already in place for the existing homepage and was right for content-heavy storytelling. HubSpot CMS made sense for the RevOps offering because the service itself runs on HubSpot — the platform choice doubled as a product demo.</p>
        </div>

        <div className="case-divider" />

        {/* ── 03 Stakeholder Discovery ── */}
        <div className="case-section">
          <span className="case-section-num">03 — Discovery</span>
          <h2>Stakeholder <em>Discovery</em></h2>
          <p>I led discovery sessions with three CANI leaders across sales, marketing, and product. The goal was to map what each team needed the sites to <em>do</em>, not just what they should <em>say</em>.</p>
          <p>Key findings:</p>
          <ul>
            <li><strong>Sales</strong> needed the main site to act as a credibility check — partners visible, services scannable, contact accessible.</li>
            <li><strong>Marketing</strong> needed the HubSpot site to convert RevOps-curious traffic into assessment requests without forcing visitors through a generic contact form.</li>
            <li><strong>Product</strong> needed the RevOps service positioned as deeply integrated with HubSpot's own AI tools (Breeze Assistant, Breeze Intelligence, Breeze Agents) — not as a generic AI consultancy.</li>
          </ul>
          <p>These three perspectives shaped the information architecture for both sites.</p>
        </div>

        <div className="case-divider" />

        {/* ── 04 Shared Design System ── */}
        <div className="case-section">
          <span className="case-section-num">04 — Design</span>
          <h2>Shared <em>Design System</em></h2>
          <p>Even though the two sites would live on different platforms with different goals, they had to feel like the same company. I built a shared design system in Figma that covered both:</p>
          <ul>
            <li><strong>Typography</strong> — a single type system: a clean modern sans for body and headings, with consistent type scale across both properties</li>
            <li><strong>Color palette</strong> — CANI's existing navy paired with a gold accent for primary actions and emphasis (drawn from their logo), with extended neutrals</li>
            <li><strong>Component library</strong> — buttons, cards, navigation, partner blocks, capability cards, and hero patterns that worked across WordPress blocks and HubSpot modules</li>
            <li><strong>Navigation patterns</strong> — different IA per site, but consistent visual language and interaction patterns</li>
          </ul>
          <p>The design system meant that someone moving between the two sites felt continuity, even though each site was optimized for a different decision.</p>
        </div>

        <div className="case-divider" />

        {/* ── 05 The Main Site (WordPress) ── */}
        <div className="case-section">
          <span className="case-section-num">05 — Build</span>
          <h2>The Main <em>Site (WordPress)</em></h2>
          <p>Built to tell CANI's full story.</p>
          <ul>
            <li><strong>Hero</strong> — clear positioning ("Helping Organizations Modernize, Execute and Deliver Results"), paired with a real photo of the team to ground the brand in people, not stock graphics</li>
            <li><strong>Services overview</strong> — five service areas (acquisition support, contract management, IT modernization, workforce training, cybersecurity) each with their own depth</li>
            <li><strong>Partners section</strong> — featured HubSpot, HireVue, and Adaptive Security with one-line value props and visual product previews, positioning CANI as a connector to best-in-class platforms</li>
            <li><strong>Capability deep-dives</strong> — pages like "Modern Security Training At Scale" pulled visitors deeper into specific solution areas</li>
          </ul>

          <div className="case-img">
            <img src="/images/cani-main-hero.png" alt="CANI main site homepage" />
          </div>
          <div className="case-img">
            <img src="/images/cani-main-partners.png" alt="CANI Partners section: HubSpot, HireVue, Adaptive Security" />
          </div>
          <div className="case-img">
            <img src="/images/cani-main-adaptive.png" alt="CANI Adaptive Security capability deep-dive" />
          </div>
        </div>

        <div className="case-divider" />

        {/* ── 06 The HubSpot Site ── */}
        <div className="case-section">
          <span className="case-section-num">06 — Build</span>
          <h2>The HubSpot <em>Site</em></h2>
          <p>A different kind of site — more focused, more conversion-oriented.</p>
          <ul>
            <li><strong>Hero</strong> — product-forward headline ("AI-Powered Revenue Systems, Designed for Growth") with two clear CTAs: Get a RevOps Assessment or Speak to a Strategist</li>
            <li><strong>What changes when it works</strong> — a numbered results section walking through the outcomes of a strong RevOps foundation</li>
            <li><strong>We don't implement AI, we operationalize it</strong> — three approach cards (Practical Over Experimental, Integrated by Design, Native First) that differentiate CANI from generic AI consultancies</li>
            <li><strong>AI Applied Across Your Customer Journey</strong> — marketing/sales/service breakdowns that map specific AI applications to the buyer journey</li>
          </ul>
          <p>The structural difference was deliberate: the WordPress site lets you wander, the HubSpot site funnels you toward a conversation.</p>

          <div className="case-img">
            <img src="/images/cani-hubspot-hero.png" alt="CANI HubSpot site hero" />
          </div>
          <div className="case-img">
            <img src="/images/cani-hubspot-handle.png" alt="CANI HubSpot site: Everything Your Implementation Needs" />
          </div>
          <div className="case-img">
            <img src="/images/cani-hubspot-results.png" alt="CANI HubSpot site: What Changes When It Works" />
          </div>
        </div>

        <div className="case-divider" />

        {/* ── 07 Accessibility ── */}
        <div className="case-section">
          <span className="case-section-num">07 — Standards</span>
          <h2><em>Accessibility</em></h2>
          <p>All layouts were tested against WCAG 2.1 AA standards. Color contrast, semantic structure, keyboard navigation, and responsive behavior were all verified before launch on both platforms.</p>
        </div>

        <div className="case-divider" />

        {/* ── 08 Project Snapshot ── */}
        <div className="case-section">
          <span className="case-section-num">08 — At a Glance</span>
          <h2>Project <em>Snapshot</em></h2>
          <div className="outcome-grid">
            <div className="outcome-cell">
              <div className="outcome-number">12wk</div>
              <div className="outcome-desc">Total project timeline from kickoff to dual launch</div>
            </div>
            <div className="outcome-cell">
              <div className="outcome-number">2</div>
              <div className="outcome-desc">Production sites delivered on different platforms</div>
            </div>
            <div className="outcome-cell">
              <div className="outcome-number">3</div>
              <div className="outcome-desc">Stakeholder leaders engaged across sales, marketing, and product</div>
            </div>
            <div className="outcome-cell">
              <div className="outcome-number">WCAG</div>
              <div className="outcome-desc">2.1 AA accessibility standard met across both properties</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
